// Regex patterns for MinKNOW directory parsing and validation
export const CLASSIC_PATH_PATTERN = /(?:^|.*\/)(\d{8})_(\d{4})_([^_]+)_([^_]+)_([a-z0-9]+)$/i;
export const NEW_PATH_PATTERN = /^(?:.*\/)?(\d{8}|\d{6})_(.+)$/i;
export const SAMPLE_DIR_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9_-]*$/;
export const CLASSIC_FASTQ_PATTERN = /^([^_]+)_pass_(barcode\d+[a-z]?|unclassified)_[^_]+_[^_]+_(\d+)\.fastq\.gz$/i;
export const FASTQ_PATTERN = /^.+_(\d+)\.fastq\.gz$/i;
export const SAMPLE_SHEET_PATTERN = /^sample_sheet_.+\.csv$/i;
export const FINAL_SUMMARY_PATTERN = /^final_summary_.+\.txt$/i;

/**
 * Extracts MinKNOW metadata from a directory path.
 * @param {string} path
 * @returns {object|null}
 */
export function parseMinKNOWPath(path) {
  const classicMatch = CLASSIC_PATH_PATTERN.exec(path);
  if (classicMatch) {
    const [, dateStr, timeStr, sequencerSerialNumber, flowCellSerialNumber, protocolRunId] = classicMatch;

    const date = `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
    const time = `${timeStr.slice(0, 2)}:${timeStr.slice(2, 4)}`;

    return {
      format: 'classic',
      date,
      time,
      sequencerSerialNumber,
      flowCellSerialNumber,
      protocolRunId,
      datasetName: generateDatasetName({ flowCellSerialNumber, date, time, protocolRunId })
    };
  }

  const newMatch = NEW_PATH_PATTERN.exec(path);
  if (!newMatch) {
    return null;
  }

  const [, dateStr, runLabel] = newMatch;
  const date = dateStr.length === 8
    ? `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`
    : `20${dateStr.slice(4, 6)}-${dateStr.slice(0, 2)}-${dateStr.slice(2, 4)}`;
  const directoryName = `${dateStr}_${runLabel}`;

  return {
    format: 'new',
    date,
    runLabel,
    datasetName: generateDatasetName({ directoryName })
  };
}

/**
 * Validates sample directory names (alphanumeric, dashes, underscores; no spaces).
 * @param {string} name
 * @returns {boolean}
 */
export function isValidSampleDirectory(name) {
  return SAMPLE_DIR_PATTERN.test(name || '');
}

/**
 * Extracts the numeric pod5 index from a FASTQ filename.
 * @param {string} filename
 * @returns {number|null}
 */
export function extractPod5Index(filename) {
  const match = FASTQ_PATTERN.exec(filename);
  if (!match) {
    return null;
  }

  const index = Number.parseInt(match[1], 10);
  return Number.isNaN(index) ? null : index;
}

/**
 * Sorts an array of File objects (or objects with .name) by pod5 index ascending.
 * @param {Array<{name: string}>} files
 * @returns {Array}
 */
export function sortByPod5Index(files) {
  return [...files].sort((a, b) => {
    const indexA = extractPod5Index(a?.name || '');
    const indexB = extractPod5Index(b?.name || '');

    if (indexA === null && indexB === null) return 0;
    if (indexA === null) return 1;
    if (indexB === null) return -1;

    return indexA - indexB;
  });
}

/**
 * Generates dataset name using flow cell serial number, date (YYYY-MM-DD), time, and protocol run id.
 * Time accepts HH:MM or HHMM and normalizes to HH-MM for dataset naming.
 * @param {object} metadata
 * @param {string} metadata.flowCellSerialNumber
 * @param {string} metadata.date
 * @param {string} metadata.time
 * @param {string} metadata.protocolRunId
 * @returns {string|null}
 */
export function generateDatasetName({ flowCellSerialNumber, date, time, protocolRunId, directoryName }) {
  if (directoryName) {
    return directoryName;
  }

  if (!flowCellSerialNumber || !date || !time || !protocolRunId) {
    return null;
  }

  const normalizedTime = time.includes(':')
    ? time.replace(':', '-')
    : `${time.slice(0, 2)}-${time.slice(2, 4)}`;

  return `${flowCellSerialNumber}-${date}-${normalizedTime}-${protocolRunId}`;
}

function normalizeFilename(file) {
  const name = file?.name || file?.path || file?.webkitRelativePath || '';
  const normalized = name.replace(/\\/g, '/');
  const segments = normalized.split('/');
  return segments[segments.length - 1] || '';
}

function normalizeCsvValue(value) {
  return (value || '').trim().replace(/^"|"$/g, '');
}

function normalizeSummaryValue(value) {
  return (value || '').trim();
}

export function parseSampleSheet(csvContent) {
  if (!csvContent) {
    return null;
  }

  const lines = csvContent.split(/\r?\n/).filter((line) => line.trim().length > 0);
  if (lines.length < 2) {
    return null;
  }

  const headers = lines[0].split(',').map((header) => normalizeCsvValue(header));
  const flowCellIdIndex = headers.indexOf('flow_cell_id');
  const flowCellProductIndex = headers.indexOf('flow_cell_product_code');
  if (flowCellIdIndex === -1 && flowCellProductIndex === -1) {
    return null;
  }

  const dataLine = lines.slice(1).find((line) => line.trim().length > 0);
  if (!dataLine) {
    return null;
  }

  const columns = dataLine.split(',').map((value) => normalizeCsvValue(value));
  const flowCellId = flowCellIdIndex > -1 ? columns[flowCellIdIndex] : '';
  const flowCellProductCode = flowCellProductIndex > -1 ? columns[flowCellProductIndex] : '';

  if (!flowCellId && !flowCellProductCode) {
    return null;
  }

  return {
    flowCellId: flowCellId || null,
    flowCellProductCode: flowCellProductCode || null
  };
}

export function parseFinalSummary(txtContent) {
  if (!txtContent) {
    return null;
  }

  const lines = txtContent.split(/\r?\n/).filter((line) => line.trim().length > 0);
  const values = {};
  for (const line of lines) {
    const [rawKey, ...rest] = line.split('=');
    if (!rawKey || !rest.length) {
      continue;
    }

    const key = normalizeSummaryValue(rawKey);
    const value = normalizeSummaryValue(rest.join('='));
    if (key && value) {
      values[key] = value;
    }
  }

  const instrument = values.instrument || '';
  const position = values.position || '';
  const flowCellId = values.flow_cell_id || '';
  const sequencerSerialNumber = instrument && position ? `${instrument}-${position}` : '';

  if (!sequencerSerialNumber && !flowCellId) {
    return null;
  }

  return {
    sequencerSerialNumber: sequencerSerialNumber || null,
    flowCellId: flowCellId || null
  };
}

export function findSupportingFiles(files = []) {
  let sampleSheetFile = null;
  let finalSummaryFile = null;

  for (const file of files) {
    const filename = normalizeFilename(file);
    if (!sampleSheetFile && SAMPLE_SHEET_PATTERN.test(filename)) {
      sampleSheetFile = file;
    }
    if (!finalSummaryFile && FINAL_SUMMARY_PATTERN.test(filename)) {
      finalSummaryFile = file;
    }
    if (sampleSheetFile && finalSummaryFile) {
      break;
    }
  }

  return { sampleSheetFile, finalSummaryFile };
}


